import { CKEditor } from '@ckeditor/ckeditor5-react';
import { WProofreader } from '@webspellchecker/wproofreader-ckeditor5';
import type { Editor, PluginConstructor } from 'ckeditor5';
import cn from 'classnames';
import { type ComponentProps, type MutableRefObject, memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateRichTextEditorStyle, useRichTextEditorReactSettings } from '../hooks';
import {
  ClipboardImageValidator,
  type ImageUploadMeta,
  type ImageUploadRequest,
  type ImageUploadedStatuses,
  MaxIndent,
  UserMention,
  imageUpload,
  serialize,
} from '../plugins';
import { type EditorConfig, RichTextEditorBase } from '../rich-text-editor-base';
import type { PluginErrorTranslationKey } from '../types';
import { colorOptions } from './defaults';

export type AnnotationOption = {
  id: string;
  link: string;
  userId: string;
};

export type RichTextData = {
  html: string;
  text: string;
};

export type RichTextMentions = (query: string) => AnnotationOption[] | Promise<AnnotationOption[]>;

export type ToolbarOption =
  | 'bold'
  | 'bulletedList'
  | 'embed'
  | 'emoji'
  | 'fontColor'
  | 'heading'
  | 'imageUpload'
  | 'indent'
  | 'italic'
  | 'link'
  | 'mediaEmbed'
  | 'numberedList'
  | 'outdent'
  | 'strikethrough'
  | 'underline'
  | 'wproofreader';

const defaultToolbarOptions: ToolbarOption[] = [
  'heading',
  'fontColor',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'numberedList',
  'bulletedList',
  'outdent',
  'indent',
  'mediaEmbed',
  'imageUpload',
  'link',
  'emoji',
];

type P = {
  data?: string;
  disabled?: boolean;
  editable?: boolean;
  editorRef?: MutableRefObject<null | RichTextEditorBase>;
  height?: 'auto' | 'small';
  isFocusedInitially?: boolean;
  isMobile?: boolean;
  labelId?: string;
  marginBottom?: boolean;
  marginTop?: boolean;
  maxHeight?: number;
  mentions?: RichTextMentions;
  minHeight?: number;
  onChange?: (data: RichTextData) => void;
  onError?: ComponentProps<typeof CKEditor<RichTextEditorBase>>['onError'];
  onImagePreview?: (sources: string[], index: number) => void;
  onReady?: ComponentProps<typeof CKEditor<RichTextEditorBase>>['onReady'];
  onRequestUpload?: ImageUploadRequest;
  onUploading?: (uploading: boolean) => void;
  placeholder?: string;
  toolbar?: ToolbarOption[];
};

const classes = ['block-indent__1', 'block-indent__2', 'block-indent__3', 'block-indent__4', 'block-indent__5'];

const embedHtml = (baseUrl: string, options?: string) =>
  `<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">` +
  `<iframe src="${baseUrl}" ` +
  `style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ${options ?? ''}allowfullscreen>` +
  `</iframe>` +
  `</div>`;

const defaultMentions: AnnotationOption[] = [];

const RichTextEditor = ({
  data,
  disabled = false,
  editable = true,
  editorRef,
  height,
  isFocusedInitially = false,
  isMobile = false,
  labelId,
  marginBottom = true,
  marginTop = true,
  maxHeight,
  mentions,
  minHeight,
  onChange,
  onError,
  onImagePreview,
  onReady,
  onRequestUpload,
  onUploading,
  placeholder,
  toolbar: toolbarItems = defaultToolbarOptions,
}: P) => {
  const { i18n, t } = useTranslation();
  const { onError: onErrorProvided, translations } = useRichTextEditorReactSettings();
  const uploadData = useRef<ImageUploadedStatuses>({});
  const metaData = useRef<ImageUploadMeta>({});
  const editorStyle = useCreateRichTextEditorStyle({ maxHeight, minHeight });

  const onHandleKeyError = useCallback(
    (key: PluginErrorTranslationKey) => {
      onErrorProvided(translations.errors[key]);
    },
    [onErrorProvided, translations.errors],
  );

  const onChangeUploading = useCallback(
    (uploading: boolean) => {
      onUploading?.(uploading);
    },
    [onUploading],
  );

  const onUpdateData = useCallback((editor: Editor) => onChange?.(serialize(editor)), [onChange]);

  const toolbarRemoveItems: ToolbarOption[] = [];
  const extraPlugins: PluginConstructor[] = [UserMention];
  const removePlugins: Array<PluginConstructor | string> = [];

  if (editable) {
    extraPlugins.push(ClipboardImageValidator, MaxIndent);
  } else {
    removePlugins.push(WProofreader);
  }

  // TODO: Create propper plugin
  if (onRequestUpload) {
    extraPlugins.push(
      imageUpload(
        onRequestUpload,
        onChangeUploading,
        onUpdateData,
        onHandleKeyError,
        // eslint-disable-next-line react-hooks/refs -- TODO: fix later
        uploadData.current,
        // eslint-disable-next-line react-hooks/refs -- TODO: fix later
        metaData.current,
      ),
    );
  } else {
    toolbarRemoveItems.push('imageUpload');
  }

  const shouldShowToolbar = editable && toolbarItems.length > 0;

  const editorConfiguration: EditorConfig = {
    clipboardImageValidator: {
      onError: onHandleKeyError,
    },
    extraPlugins,
    fontColor: {
      colorPicker: false,
      colors: colorOptions.map((color) => ({ ...color, label: t(translations.colors[color.label]) })),
      columns: 6,
    },
    heading: {
      options: [
        { class: '', model: 'paragraph', title: 'P' },
        { class: '', model: 'heading1', title: 'H1', view: 'h1' },
        { class: '', model: 'heading2', title: 'H2', view: 'h2' },
      ],
    },
    imagePreview: {
      onClick: onImagePreview,
    },
    indentBlock: {
      classes,
    },
    language: i18n.language.slice(0, 2),
    licenseKey: 'GPL',
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
    },
    list: {
      multiBlock: false,
    },
    maxIndent: {
      max: classes.length,
    },
    mediaEmbed: {
      extraProviders: [
        {
          html(match: string[]) {
            const presentationKey = match[4];

            if (!presentationKey) {
              return '';
            }

            return embedHtml(`https://docs.google.com/presentation/d/${presentationKey}/embed`);
          },
          name: 'googleSlides',

          // eslint-disable-next-line prefer-named-capture-group, regexp/no-empty-alternative -- ReGex should be named or non-capturing
          url: /(((https|http):\/\/|)docs\.google\.com\/presentation\/d\/)(.+?(?=(\/.+|\/|$)))/,
        },
        {
          html(match: string[]) {
            const id = match[1];

            return embedHtml(`https://www.youtube.com/embed/${id}`, 'allow="autoplay; encrypted-media" ');
          },
          name: 'youtubeLive',
          // eslint-disable-next-line prefer-named-capture-group -- ReGex should be named or non-capturing
          url: /^youtube\.com\/live\/(.*)$/,
        },
      ],
      removeProviders: ['instagram', 'twitter', 'googleMaps', 'flickr', 'facebook'],
    },
    mention: {
      feeds: [
        {
          feed: mentions ?? defaultMentions,
          marker: '@',
          minimumCharacters: 1,
        },
      ],
    },
    placeholder: placeholder ? t(placeholder) : placeholder,
    removePlugins,
    toolbar: shouldShowToolbar
      ? {
          items: toolbarItems,
          removeItems: toolbarRemoveItems,
          shouldNotGroupWhenFull: true,
        }
      : undefined,
    typing: {
      transformations: {
        extra: [
          {
            // eslint-disable-next-line prefer-named-capture-group -- ReGex should be named or non-capturing
            from: /([!.?] )([a-z])$/,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Value is required
            to: (matches: string[]) => [null, matches[1]!.toUpperCase()],
          },
          {
            // eslint-disable-next-line prefer-named-capture-group -- ReGex should be named or non-capturing
            from: /^([a-z])$/,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Value is required
            to: (matches: string[]) => [matches[0]!.toUpperCase()],
          },
        ],
        include: [],
      },
    },
    ui: {
      poweredBy: {
        label: null,
        position: 'inside',
        side: 'left',
      },
    },
    wproofreader: {
      actionItems: isMobile ? ['settings', 'toggle'] : ['settings', 'toggle', 'proofreadDialog'],
      autocomplete: !isMobile,
      serviceId: 'LhzEvM4RdIyDCUr',
      srcUrl: 'https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js',
    },
  };

  return (
    <div
      className={cn('rich-text', {
        'rich-text--auto-height': height === 'auto',
        'rich-text--hide-toolbar': !shouldShowToolbar,
        'rich-text--no-margin-bottom': !marginBottom,
        'rich-text--no-margin-top': !marginTop,
        'rich-text--small-height': height === 'small',
      })}
      style={editorStyle}
    >
      <CKEditor<RichTextEditorBase>
        config={editorConfiguration}
        data={data}
        disabled={disabled}
        editor={RichTextEditorBase}
        onChange={(_, editor) => {
          onUpdateData(editor);
        }}
        onError={onError}
        onReady={(editor) => {
          if (isFocusedInitially) {
            editor.focus();
          }

          if (editable) {
            if (editorRef) {
              editorRef.current = editor;
            }

            editor.disableReadOnlyMode('editable');
          } else {
            editor.enableReadOnlyMode('editable');
          }

          if (labelId) {
            editor.editing.view.document.getRoot()?._setAttribute('aria-label', '');
            editor.editing.view.document.getRoot()?._setAttribute('aria-labelledby', labelId);
          }

          onReady?.(editor);
        }}
      />
    </div>
  );
};

export default memo(RichTextEditor);
